import { sanitizeVariableName } from "../../../utils/formulaScope";
import type { LineEdits } from "../types";
import type { MaterialLineItemOption } from "../types";
import { buildCategoryEditKeys } from "./buildCategoryEditKeys";
import type { MaterialRowItem } from "./materialLineItems";

function idsMatch(selectedId: string, candidateId: string): boolean {
    return (
        selectedId === candidateId ||
        selectedId.startsWith(`${candidateId}-`) ||
        candidateId.startsWith(`${selectedId}-`)
    );
}

export function resolveSelectedProductName(
    productId: string,
    options: MaterialLineItemOption[],
): string | null {
    for (const option of options) {
        if (idsMatch(productId, option.ProductId)) return option.ProductName;
        const variantHit = option.Variants?.some((v) =>
            idsMatch(productId, v.ProductId),
        );
        if (variantHit) return option.ProductName;
    }
    return null;
}

/**
 * Expose material product selections on the shared formula scope
 * (e.g. FieldShingles_Selection, FieldShingles_SelectedProductId).
 */
export function buildMaterialSelectionsScope(
    materialsEdits: LineEdits | undefined,
    materialsByGroup: Record<string, MaterialRowItem[]> | undefined,
): Record<string, string> {
    const scope: Record<string, string> = {
        FieldShingles_Selection: "",
        FieldShingles_SelectedProductId: "",
    };

    if (!materialsEdits || !materialsByGroup) return scope;

    const { editKeyByCategory, optionsByEditKey } =
        buildCategoryEditKeys(materialsByGroup);

    for (const [category, editKey] of Object.entries(editKeyByCategory)) {
        const productId = materialsEdits[editKey]?.selectedProductId ?? null;
        if (!productId) continue;

        const options = optionsByEditKey[editKey] ?? [];
        const productName =
            resolveSelectedProductName(productId, options) ?? "";
        const base = sanitizeVariableName(category);

        scope[`${base}_Selection`] = productName;
        scope[`${base}_SelectedProductId`] = productId;
    }

    return scope;
}
